import React, { useEffect, useRef, useState } from "react"
import Title from "./title"
import Imgix from "react-imgix"
import styles from "../styles/scss/components/content.module.scss"
import cn from "classnames"
import VideoBlock from "./videoBlock"
import Gallery from "./gallery"
import ImageBlock from "./imageBlock"
import RelatedArticlesBlock from "./relatedArticlesBlock"
import Card from "./card"
import ProductsUsed from "./productsUsed"
import { supSub, ensureImgAlt, removeEmptyAnchors } from "../services/util"

const getBlockType = block => {
    if (!block) return "unknown_block"
    if (block.__typename) return block.__typename
    if (block.model && block.model.apiKey) return block.model.apiKey
    return "unknown_block"
}

const getBlockKey = (block, index) => {
    if (block && block.id) return block.id
    return `${getBlockType(block)}-${index}`
}

const getErrorLocation = error => {
    if (!error || !error.stack) return ""
    const stackLines = error.stack
        .split("\n")
        .map(line => line.trim())
        .filter(Boolean)
    const fileLine = stackLines.find(line => line.indexOf(".js:") !== -1)
    return fileLine || stackLines[0] || ""
}

const renderBlockDiagnostic = ({ key, message, details }) => (
    <section key={key} className={styles.textRow}>
        <div style={{ border: "1px dashed #b71c1c", padding: "12px", color: "#b71c1c" }}>
            <strong>Content block warning:</strong> {message}
            {details && <div style={{ marginTop: "6px", fontSize: "0.9em" }}>{details}</div>}
        </div>
    </section>
)

const normalizeLayoutValue = value => {
    if (value === null || value === undefined) return ""
    return String(value).trim().toLowerCase()
}

const getGapSize = gap => {
    const gapMap = {
        none: 0,
        xs: 4,
        sm: 8,
        md: 16,
        lg: 24,
        xl: 32,
    }
    const key = normalizeLayoutValue(gap)
    return Object.prototype.hasOwnProperty.call(gapMap, key) ? gapMap[key] : undefined
}

const getRowLayoutClasses = row => {
    const classes = ["row", "d-flex"]

    if (row && row.stackOnMobile === true) {
        classes.push("flex-column", "flex-md-row", styles.stackedMobileRow)
    }

    const justifyMap = {
        start: "justify-content-start",
        center: "justify-content-center",
        end: "justify-content-end",
        between: "justify-content-between",
        around: "justify-content-around",
    }
    const alignMap = {
        start: "align-items-start",
        center: "align-items-center",
        end: "align-items-end",
        stretch: "align-items-stretch",
        baseline: "align-items-baseline",
    }
    const wrapMap = {
        nowrap: "flex-nowrap",
        wrap: "flex-wrap",
    }

    const justifyClass = justifyMap[normalizeLayoutValue(row && row.justify)]
    const alignClass = alignMap[normalizeLayoutValue(row && row.alignItems)]
    const wrapClass = wrapMap[normalizeLayoutValue(row && row.wrap)]

    if (justifyClass) classes.push(justifyClass)
    if (alignClass) classes.push(alignClass)
    if (wrapClass) classes.push(wrapClass)

    return classes
}

const getRowLayoutStyle = row => {
    const style = {}
    const justify = normalizeLayoutValue(row && row.justify)
    const gapSize = getGapSize(row && row.gap)

    if (justify === "evenly") {
        style.justifyContent = "space-evenly"
    }

    if (gapSize !== undefined) {
        style.rowGap = `${gapSize}px`
        style.columnGap = `${gapSize}px`
    }

    return style
}

const getColumnLayoutClasses = column => {
    const classes = ["d-flex", "flex-column"]

    const widthMap = {
        auto: "col",
        full: "col-12",
        "1-2": "col-12 col-md-6",
        "1-3": "col-12 col-md-4",
        "2-3": "col-12 col-md-8",
        "1-4": "col-12 col-md-3",
        "3-4": "col-12 col-md-9",
    }
    const alignSelfMap = {
        start: "align-self-start",
        center: "align-self-center",
        end: "align-self-end",
        stretch: "align-self-stretch",
    }
    const textAlignMap = {
        left: "text-left",
        center: "text-center",
        right: "text-right",
    }

    const widthClass = widthMap[normalizeLayoutValue(column && column.width)] || "col"
    const alignSelfClass = alignSelfMap[normalizeLayoutValue(column && column.alignSelf)]
    const textAlignClass = textAlignMap[normalizeLayoutValue(column && column.textAlign)]

    widthClass.split(" ").forEach(c => classes.push(c))
    if (alignSelfClass) classes.push(alignSelfClass)
    if (textAlignClass) classes.push(textAlignClass)

    return classes
}

const getColumnLayoutStyle = column => {
    const style = {}
    const justifyMap = {
        start: "flex-start",
        center: "center",
        end: "flex-end",
        between: "space-between",
    }
    const paddingMap = {
        none: "0px",
        sm: "8px",
        md: "16px",
        lg: "24px",
    }

    const justifyContent = justifyMap[normalizeLayoutValue(column && column.justifyContent)]
    const padding = paddingMap[normalizeLayoutValue(column && column.padding)]

    if (justifyContent) {
        style.justifyContent = justifyContent
    }
    if (padding) {
        style.padding = padding
    }

    return style
}

const CMS_LINK_CLASS_ALIASES = {
    pill: "linkPillButton",
    "button-pill": "linkPillButton",
    "button-pill-red": "linkPillButton",
    "link-pill": "linkPillButton",
}

const resolveCmsLinkClassName = className => {
    const raw = typeof className === "string" ? className.trim() : ""
    if (!raw) return ""

    const tokens = raw.split(/\s+/).filter(Boolean)
    const mapped = []
    const passthrough = []

    tokens.forEach(token => {
        const alias = CMS_LINK_CLASS_ALIASES[token]
        if (alias && styles[alias]) {
            mapped.push(styles[alias])
        } else {
            passthrough.push(token)
        }
    })

    return [...mapped, ...passthrough].join(" ")
}

const normalizeCarouselSize = size => {
    const value = normalizeLayoutValue(size)
    if (value === "small" || value === "medium" || value === "large") return value
    return "medium"
}

const normalizeCarouselShape = shape => {
    const value = normalizeLayoutValue(shape)
    if (value === "square" || value === "wide" || value === "tall") return value
    return "wide"
}

const normalizeCarouselTitleAlignment = alignment => {
    const value = normalizeLayoutValue(alignment)
    if (value === "center" || value === "left") return value
    return "left"
}

const CAROUSEL_THUMB_DIMENSIONS = {
    small: {
        wide: { w: 280, h: 200 },
        square: { w: 280, h: 280 },
        tall: { w: 200, h: 280 },
    },
    medium: {
        wide: { w: 420, h: 300 },
        square: { w: 420, h: 420 },
        tall: { w: 300, h: 420 },
    },
    large: {
        wide: { w: 560, h: 400 },
        square: { w: 560, h: 560 },
        tall: { w: 400, h: 560 },
    },
}

const getCarouselThumbDimensions = (size, shape) => {
    const normalizedSize = normalizeCarouselSize(size)
    const normalizedShape = normalizeCarouselShape(shape)
    const sizeConfig = CAROUSEL_THUMB_DIMENSIONS[normalizedSize]
    return sizeConfig[normalizedShape] || CAROUSEL_THUMB_DIMENSIONS.medium.wide
}

const ImageCarouselBlock = ({ images, blockKey, size, shape, title, titleAlignment }) => {
    const [activeIndex, setActiveIndex] = useState(0)
    const [isExpanded, setIsExpanded] = useState(false)
    const hasImages = Array.isArray(images) && images.length > 0
    const trackRef = useRef(null)
    const normalizedSize = normalizeCarouselSize(size)
    const normalizedShape = normalizeCarouselShape(shape)
    const thumbDimensions = getCarouselThumbDimensions(normalizedSize, normalizedShape)
    const normalizedTitleAlignment = normalizeCarouselTitleAlignment(titleAlignment)

    useEffect(() => {
        if (!isExpanded) return undefined

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = "hidden"

        const onKeyDown = event => {
            if (event.key === "Escape") {
                setIsExpanded(false)
            }
            if (event.key === "ArrowLeft") {
                setActiveIndex(current => (current - 1 + images.length) % images.length)
            }
            if (event.key === "ArrowRight") {
                setActiveIndex(current => (current + 1) % images.length)
            }
        }

        window.addEventListener("keydown", onKeyDown)

        return () => {
            window.removeEventListener("keydown", onKeyDown)
            document.body.style.overflow = previousOverflow
        }
    }, [isExpanded, images])

    useEffect(() => {
        if (!hasImages) return
        const track = trackRef.current
        if (!track || !images[activeIndex]) return

        const activeThumb = track.querySelector(`[data-thumb-index="${activeIndex}"]`)
        if (!activeThumb) return

        activeThumb.scrollIntoView({
            inline: "center",
            block: "nearest",
            behavior: "smooth",
        })
    }, [activeIndex, hasImages, images])

    if (!hasImages) return null

    const goToPrevious = () => {
        setActiveIndex(current => (current - 1 + images.length) % images.length)
    }

    const goToNext = () => {
        setActiveIndex(current => (current + 1) % images.length)
    }

    const activeImage = images[activeIndex]

    return (
        <section
            className={cn(
                styles.imageCarousel,
                styles[`imageCarouselSize${normalizedSize.charAt(0).toUpperCase()}${normalizedSize.slice(1)}`],
                styles[`imageCarouselShape${normalizedShape.charAt(0).toUpperCase()}${normalizedShape.slice(1)}`]
            )}
            key={blockKey}
        >
            {title !== null && title !== undefined && (
                <div className={styles.imageCarouselTitleRow}>
                    <Title
                        titleType="h2"
                        className={cn(
                            styles.title,
                            styles.imageCarouselTitle,
                            normalizedTitleAlignment === "center"
                                ? styles.imageCarouselTitleAlignCenter
                                : styles.imageCarouselTitleAlignLeft
                        )}
                        value={title}
                    />
                </div>
            )}
            <div className={styles.imageCarouselViewport}>
                <button
                    type="button"
                    className={styles.imageCarouselChevron}
                    onClick={goToPrevious}
                    aria-label="Previous image"
                >
                    &#8249;
                </button>
                <div className={styles.imageCarouselTrack} ref={trackRef}>
                    {images.map((image, index) => (
                        <button
                            key={image.id || `${blockKey}-thumb-${index}`}
                            type="button"
                            data-thumb-index={index}
                            className={cn(styles.imageCarouselThumb, {
                                [styles.imageCarouselThumbActive]: index === activeIndex,
                            })}
                            onClick={() => {
                                setActiveIndex(index)
                                setIsExpanded(true)
                            }}
                            aria-label={`Open image ${index + 1} in fullscreen`}
                        >
                            <Imgix
                                src={image.url}
                                className={styles.imageCarouselThumbImage}
                                disableSrcSet={true}
                                imgixParams={{
                                    fit: "crop",
                                    w: thumbDimensions.w,
                                    h: thumbDimensions.h,
                                    auto: "format,compress",
                                    q: 70,
                                }}
                                htmlAttributes={{
                                    alt: image.alt || image.title || `Carousel image ${index + 1}`,
                                    width: thumbDimensions.w,
                                    height: thumbDimensions.h,
                                }}
                            />
                        </button>
                    ))}
                </div>
                <button
                    type="button"
                    className={styles.imageCarouselChevron}
                    onClick={goToNext}
                    aria-label="Next image"
                >
                    &#8250;
                </button>
            </div>

            {isExpanded && (
                <div className={styles.imageCarouselModal} role="dialog" aria-modal="true" aria-label="Fullscreen image carousel">
                    <button
                        type="button"
                        className={styles.imageCarouselClose}
                        onClick={() => setIsExpanded(false)}
                        aria-label="Close fullscreen carousel"
                    >
                        &#10005;
                    </button>
                    <div className={styles.imageCarouselModalInner}>
                        <button
                            type="button"
                            className={styles.imageCarouselChevron}
                            onClick={goToPrevious}
                            aria-label="Previous fullscreen image"
                        >
                            &#8249;
                        </button>
                        <Imgix
                            src={activeImage.url}
                            className={styles.imageCarouselModalImage}
                            imgixParams={{ fit: "max", w: 1800, h: 1350, auto: "format,compress", q: 80 }}
                            htmlAttributes={{
                                alt:
                                    activeImage.alt ||
                                    activeImage.title ||
                                    `Fullscreen image ${activeIndex + 1}`,
                            }}
                        />
                        <button
                            type="button"
                            className={styles.imageCarouselChevron}
                            onClick={goToNext}
                            aria-label="Next fullscreen image"
                        >
                            &#8250;
                        </button>
                    </div>
                </div>
            )}
        </section>
    )
}

const buildContents = props => {
    const { content, pageTitle = "", depth = 0, parentType = "", reviews = [] } = props
    if (content === null || content === undefined) {
        return ""
    }

    var out = []
    var curTextImageRow = 0
    const formatHtml = (html) => removeEmptyAnchors(ensureImgAlt(supSub(html)))
    const ctObjs = Object.values(content)
    for (var i = 0; i < ctObjs.length; i++) {
        const c = ctObjs[i]
        try {
            switch (c.__typename) {
                case "DatoCMS_TextRecord":
                case "DatoCMS_SectionRecord":
                    if (!c.body || !String(c.body).trim()) {
                        break
                    }
                    out.push(
                        <section key={getBlockKey(c, i)} className={styles.textRow}>
                            {pageTitle !== c.subtitle && c.subtitle && (
                                <Title titleType="h2" className={styles.title} value={supSub(c.subtitle)} />
                            )}
                            {c.body && (
                                <div
                                className={styles.body}
                                    dangerouslySetInnerHTML={{
                                        __html: formatHtml(c.body),
                                    }}
                                />
                            )}
                        </section>
                    )
                    break

                case "DatoCMS_TextAndImageRecord":
                    const SocialAnchors = props => {
                        const { anchors, title } = props
                        const socialMediaClasses = {
                            Facebook: "fa fa-facebook-official",
                            Twitter: "fa fa-twitter",
                            Instagram: "fa fa-instagram",
                            Youtube: "fa fa-youtube-play",
                        }
                        return anchors.map((item, key) => {
                            const platform = item.class || "social media"
                            const entityName = title || ""
                            const ariaLabel = entityName
                                ? `Visit ${entityName} on ${platform}`
                                : `Visit ${platform}`

                            return (
                                <a
                                    key={key}
                                    href={item.url}
                                    className={item.class ? item.class.toLowerCase() : ""}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={ariaLabel}
                                >
                                    <i
                                        className={socialMediaClasses[item.class] ? socialMediaClasses[item.class] : ""}
                                        aria-hidden="true"
                                    />
                                </a>
                            )
                        })
                    }
                    var isEvenRow = Math.abs(curTextImageRow % 2) === 1
                    var imageCol = (
                        <div className={cn("col-sm-6", "col-md-4", "image-container")}>
                            <Imgix
                                src={c.image.url}
                                style={{ width: "100%" }}
                                sizes="(min-width: 1200px) 380px, (min-width: 768px) 33vw, (min-width: 576px) 50vw, 100vw"
                                imgixParams={{ fm: "jpg" }}
                                htmlAttributes={{ alt: c.image.alt || c.title || "" }}
                            />
                        </div>
                    )
                    var textCol = (
                        <div
                            key={c.id}
                            className={cn("col-sm-6", "col-md-8", {
                                "text-md-right": !isEvenRow,
                            })}
                        >
                            {c.title && <h3>{c.title}</h3>}
                            {c.body && (
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: formatHtml(c.body),
                                    }}
                                />
                            )}
                            {c.extralinks && c.extralinks.length > 0 && (
                                <div className={styles.socialAnchors}>
                                    <SocialAnchors anchors={c.extralinks} title={c.title} />
                                </div>
                            )}
                        </div>
                    )

                    out.push(
                        <div key={c.id}>
                            <div
                                className={cn(
                                    "row",
                                    "d-none",
                                    "d-sm-flex",
                                    styles.textAndImageRow
                                )}
                            >
                                {isEvenRow ? (
                                    <>
                                        <>{imageCol}</>
                                        <>{textCol}</>
                                    </>
                                ) : (
                                    <>
                                        <>{textCol}</>
                                        <>{imageCol}</>
                                    </>
                                )}
                            </div>
                            <div
                                className={cn(
                                    "xs-only",
                                    "row",
                                    "d-flex",
                                    "d-sm-none",
                                    styles.textAndImageRow
                                )}
                            >
                                <>{imageCol}</>
                                <>{textCol}</>
                            </div>
                        </div>
                    )
                    curTextImageRow++
                    break

                case "DatoCMS_TextAndImageHeaderRecord":
                    out.push(
                        <div key={c.id} className={styles.banner}>
                            <div className={styles.textBanner}>
                                <Title
                                    value={c.title}
                                    alignLeft
                                    className={styles.title}
                                />
                                <span
                                    className={styles.text}
                                    dangerouslySetInnerHTML={{
                                        __html: formatHtml(c.body),
                                    }}
                                />
                            </div>
                            <ImageBlock
                                image={c.image.url}
                                containerClass={styles.containerImageHeader}
                                imageClass={styles.image}
                            />
                        </div>
                    )
                    break

                case "DatoCMS_TextAndVideoHeaderRecord":
                    out.push(
                        <div key={c.id} className={styles.banner}>
                            <div className={styles.textBanner}>
                                <Title value={c.title} alignLeft className={styles.title} />
                                <span className={styles.text} dangerouslySetInnerHTML={{ __html: formatHtml(c.body) }} />
                            </div>
                            <VideoBlock
                                youtubeVideoId={c.video.providerUid}
                                image={c.video.thumbnailUrl}
                                hideBody
                                containerClass={styles.containerImageBanner}
                                imageClass={styles.image}
                            />
                        </div>
                    )
                    break

                case "DatoCMS_VideoPlayerRecord":
                    out.push(
                        <VideoBlock
                            key={c.video.providerUid}
                            imageClass={styles.imageVideo}
                            containerClass={styles.containerVideo}
                            hideBody
                            youtubeVideoId={c.video.providerUid}
                            image={c.video.thumbnailUrl}
                        />
                    )
                    break

                case "DatoCMS_HtmlRecord":
                    out.push(
                        <div
                            key={c.id}
                            dangerouslySetInnerHTML={{ __html: removeEmptyAnchors(ensureImgAlt(c.html)) }}
                        />
                    )
                    break

                case "DatoCMS_ImageRecord":
                    out.push(
                        <div key={c.image.id} className={styles.bannerB}>
                            <Imgix
                                src={c.image.url}
                                sizes="(min-width: 1200px) 1150px, 100vw"
                                imgixParams={{ fit: "max" }}
                                htmlAttributes={{ alt: c.image.alt || "" }}
                            />
                        </div>
                    )
                    break

                case "DatoCMS_ImageGalleryRecord":
                    out.push(
                        <div key={(c.images && c.images[0] && c.images[0].id) || getBlockKey(c, i)}>
                            {c.images && c.images.length > 0 && (
                                <Gallery ctaText="View the Gallery" images={c.images} title="Blog Gallery" />
                            )}
                        </div>
                    )
                    break

                case "DatoCMS_ImageCarouselRecord":
                    out.push(
                        <ImageCarouselBlock
                            key={getBlockKey(c, i)}
                            blockKey={getBlockKey(c, i)}
                            images={c.images}
                            size={c.size}
                            shape={c.shape}
                            title={c.title}
                            titleAlignment={c.titleAlignment}
                        />
                    )
                    break

                case "DatoCMS_RelatedArticleRecord":
                    out.push(
                        <RelatedArticlesBlock
                            key={getBlockKey(c, i)}
                            articles={c.articles || []}
                        />
                    )
                    break

                case "DatoCMS_LinkRecord":
                    out.push(
                        <section key={getBlockKey(c, i)} className={styles.textRow}>
                            <a href={c.url || "#"} className={resolveCmsLinkClassName(c.class)}>
                                {c.title || c.url || "Link"}
                            </a>
                        </section>
                    )
                    break

                case "DatoCMS_ProductCardRecord":
                    if (!c.product || !c.product.slug) {
                        out.push(
                            renderBlockDiagnostic({
                                key: `invalid-product-card-${getBlockKey(c, i)}`,
                                message: "product card is missing a valid product relation",
                                details: pageTitle ? `Page: ${pageTitle}` : "",
                            })
                        )
                        break
                    }

                    const productCardProduct = {
                        ...c.product,
                        videos: Array.isArray(c.product.videos) ? c.product.videos : [],
                        listImage: c.product.listImage || { url: "" },
                    }

                    out.push(
                        <div
                            key={getBlockKey(c, i)}
                            className={parentType === "DatoCMS_RowRecord" ? "col-12 col-md" : ""}
                        >
                            <Card
                                type="product"
                                title={productCardProduct.displayName}
                                image={productCardProduct.listImage ? productCardProduct.listImage.url : ""}
                                slug={productCardProduct.slug}
                                product={productCardProduct}
                            >
                                {productCardProduct.teaser && <p>{productCardProduct.teaser}</p>}
                            </Card>
                        </div>
                    )
                    break

                case "DatoCMS_ProductsUsedRecord":
                    out.push(
                        <ProductsUsed
                            key={getBlockKey(c, i)}
                            title={c.title}
                            products={c.products}
                            reviews={reviews}
                        />
                    )
                    break

                case "DatoCMS_RowRecord":
                    out.push(
                        <div
                            key={getBlockKey(c, i)}
                            className={cn(...getRowLayoutClasses(c))}
                            style={getRowLayoutStyle(c)}
                        >
                            {buildContents({
                                content: c.rowContents,
                                pageTitle,
                                depth: depth + 1,
                                parentType: "DatoCMS_RowRecord",
                                reviews,
                            })}
                        </div>
                    )
                    break

                case "DatoCMS_ColumnRecord":
                    out.push(
                        <div
                            key={getBlockKey(c, i)}
                            className={cn(...getColumnLayoutClasses(c))}
                            style={getColumnLayoutStyle(c)}
                        >
                            {buildContents({
                                content: c.columnContents,
                                pageTitle,
                                depth: depth + 1,
                                parentType: "DatoCMS_ColumnRecord",
                                reviews,
                            })}
                        </div>
                    )
                    break

                default:
                    out.push(
                        renderBlockDiagnostic({
                            key: `unsupported-block-${getBlockKey(c, i)}`,
                            message: `block type (${getBlockType(c)}) not supported`,
                            details: pageTitle ? `Page: ${pageTitle}` : "",
                        })
                    )
                    console.warn("UNHANDLED CONTENT TYPE: " + getBlockType(c))
                    break
            }
        } catch (error) {
            const blockType = getBlockType(c)
            const location = getErrorLocation(error)
            const details = [
                pageTitle ? `Page: ${pageTitle}` : "",
                location ? `Location: ${location}` : "",
                error && error.message ? `Error: ${error.message}` : "",
            ]
                .filter(Boolean)
                .join(" | ")

            out.push(
                renderBlockDiagnostic({
                    key: `errored-block-${getBlockKey(c, i)}`,
                    message: `error processing block (${blockType})`,
                    details,
                })
            )
            console.error(`Error processing content block (${blockType})`, error)
        }
    }
    if (depth === 0) {
        return out.map((block, index) => (
            <div
                key={`root-content-${index}-${(block && block.key) || "block"}`}
                className={styles.rootContentBlock}
            >
                {block}
            </div>
        ))
    }

    return out
}

export default buildContents
